import React, { useEffect, useState } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../lib/supabase';
import { ArrowLeft, PlusCircle, Trash2, LogOut, Leaf } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';

export function MembershipBucketManager() {
  const { user, userRoles, signOut } = useAuth();
  const isAdmin = userRoles.includes('admin');
  const navigate = useNavigate();

  if (!isAdmin) return null;
 
  const [buckets, setBuckets] = useState<any[]>([]);
  const [membershipNames, setMembershipNames] = useState<string[]>([]);
  const [bucketMemberships, setBucketMemberships] = useState<{ [bucketId: string]: string[] }>({});
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null);
  const [bucketName, setBucketName] = useState('');

  useEffect(() => {
    fetchBuckets();
    fetchMembershipNames();
  }, []);

  const fetchBuckets = async () => {
    const { data, error } = await supabase.from('membership_buckets').select('*');
    if (!error) {
      setBuckets(data);
      const mappingData: { [bucketId: string]: string[] } = {};
      for (const bucket of data) {
        const { data: mappings } = await supabase
          .from('membership_bucket_mappings')
          .select('membership_name')
          .eq('bucket_id', bucket.id);
        mappingData[bucket.id] = mappings?.map(m => m.membership_name) || [];
      }
      setBucketMemberships(mappingData);
    }
  };

const fetchMembershipNames = async () => {
    try {
        const { data, error } = await supabase
            .rpc('get_active_membership_names');

        if (error) {
            console.error('Error fetching membership names:', error);
            return;
        }

        if (data) {
            // The function returns an array of objects with a 'membership_name' property
            const uniqueNames = data.map((item: { membership_name: string }) => item.membership_name);
            setMembershipNames(uniqueNames);
        }
    } catch (error) {
        console.error('Unexpected error fetching membership names:', error);
    }
};

  const handleCreateBucket = async () => {
    if (!bucketName.trim()) return;
    const { error } = await supabase.from('membership_buckets').insert({ name: bucketName, owner_id: user.id });
    if (!error) {
      setBucketName('');
      fetchBuckets();
    }
  };

  const handleDeleteBucket = async (id: string) => {
    const { error } = await supabase.from('membership_buckets').delete().eq('id', id);
    if (!error) fetchBuckets();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Leaf className="h-6 w-6 text-green-600" />
              <div className="ml-2">
                <span className="text-xl font-semibold text-gray-900">THRIVE</span>
                <span className="ml-2 text-sm text-gray-500">Membership Buckets</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">{user?.email}</span>
              <button
                onClick={() => signOut()}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-4 sm:px-0">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-blue-600 opacity-90"></div>
              <div className="relative px-8 py-12 text-center">
                <h1 className="text-4xl font-bold text-white mb-2">THRIVE</h1>
                <p className="text-green-100 text-lg mb-4">
                  Tracking Health and Reducing Illness Via Efficiency
                </p>
                <div className="w-16 h-1 bg-white/30 mx-auto rounded-full"></div>
                <h2 className="text-2xl font-semibold text-white mt-4">Membership Bucket Manager</h2>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => navigate('/settings')} className="text-green-700 hover:text-green-900">
                <ArrowLeft className="h-5 w-5 inline-block mr-1" /> Back to Settings
              </button>
              <div className="flex items-center space-x-2">
                <input
                  value={bucketName}
                  onChange={(e) => setBucketName(e.target.value)}
                  placeholder="New Bucket Name"
                  className="px-3 py-1 border rounded"
                />
                <button
                  onClick={handleCreateBucket}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded flex items-center"
                >
                  <PlusCircle className="h-4 w-4 mr-1" /> Add Bucket
                </button>
              </div>
            </div>
    
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-4 rounded shadow">
                <h2 className="text-lg font-semibold mb-2">Available Memberships</h2>
                <ul className="space-y-1">
                  {membershipNames.map(name => (
                    <li
                      key={name}
                      className="px-2 py-1 bg-gray-100 rounded text-sm cursor-pointer hover:bg-gray-200"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', name);
                      }}
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
    
              <div className="md:col-span-2">
                <h2 className="text-lg font-semibold mb-4">Buckets</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {buckets.map(bucket => (
                    <div
                      key={bucket.id}
                      className="bg-white p-4 rounded shadow relative"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={async (e) => {
                        const name = e.dataTransfer.getData('text/plain');
                        if (!bucketMemberships[bucket.id]?.includes(name)) {
                          const updated = await supabase.from('membership_bucket_mappings').insert({
                            bucket_id: bucket.id,
                            membership_name: name,
                            owner_id: user.id
                          });
                          if (!updated.error) {
                            setBucketMemberships(prev => ({
                              ...prev,
                              [bucket.id]: [...(prev[bucket.id] || []), name]
                            }));
                          }
                        }
                      }}
                    >
                      <h3 className="text-md font-bold mb-2 flex justify-between items-center">
                        {bucket.name}
                        <button onClick={() => handleDeleteBucket(bucket.id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </h3>
                      <ul className="space-y-1 min-h-[40px]">
                        {(bucketMemberships[bucket.id] || []).map(name => (
                          <li key={name} className="px-2 py-1 bg-blue-100 text-blue-900 rounded text-sm">
                            {name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>           
        </div>
      </main>
    </div>
  );
}
